import Phaser from 'phaser';
import { EV } from '../game/events';
import { net } from '../net/NetClient';
import type { FriendView } from '../net/protocol';
import { COLOR, TEXT_COLOR, UI_FONT } from './theme';
import { focusAfterKey } from './focusAfterKey';

export class FriendOverlay {
  private readonly container: Phaser.GameObjects.Container;
  private readonly body: Phaser.GameObjects.Text;
  private open = false;
  private friends: FriendView[] = [];
  private incoming: string[] = [];

  constructor(scene: Phaser.Scene) {
    const w = 360;
    const h = 420;
    const x = scene.scale.width - w - 24;
    const y = 72;
    this.container = scene.add.container(x, y).setVisible(false).setScrollFactor(0).setDepth(90);

    const g = scene.add.graphics();
    g.fillStyle(COLOR.panelEdge, 0.96);
    g.fillRoundedRect(-4, -4, w + 8, h + 8, 12);
    g.fillStyle(COLOR.panel, 0.98);
    g.fillRoundedRect(0, 0, w, h, 10);
    this.container.add(g);

    const title = scene.add.text(w / 2, 14, '친구', {
      fontFamily: UI_FONT,
      fontSize: '18px',
      color: TEXT_COLOR.gold,
    });
    title.setOrigin(0.5, 0);
    this.container.add(title);

    this.body = scene.add.text(18, 48, '', {
      fontFamily: UI_FONT,
      fontSize: '13px',
      color: TEXT_COLOR.bright,
      lineSpacing: 6,
      wordWrap: { width: w - 36, useAdvancedWrap: true },
    });
    this.container.add(this.body);

    const hint = scene.add.text(
      18,
      h - 70,
      '추가: 아래 입력 후 Enter\n수락: 목록에서 닉 입력 후 수락\n/w 닉 메시지  귓속말',
      {
        fontFamily: UI_FONT,
        fontSize: '11px',
        color: TEXT_COLOR.dim,
        lineSpacing: 3,
        wordWrap: { width: w - 36, useAdvancedWrap: true },
      },
    );
    this.container.add(hint);

    net.events.on(EV.netFriends, this.onList, this);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      net.events.off(EV.netFriends, this.onList, this);
      this.close();
    });
    this.bindHtml(scene);
  }

  toggle(): boolean {
    if (this.open) this.close();
    else this.show();
    return this.open;
  }

  show(): void {
    if (!net.connected) return;
    this.open = true;
    this.container.setVisible(true);
    const dock = document.getElementById('friend-dock');
    if (dock) dock.hidden = false;
    const add = document.getElementById('friend-add') as HTMLInputElement | null;
    if (add) add.value = '';
    focusAfterKey(add);
    this.render();
  }

  close(): void {
    this.open = false;
    this.container.setVisible(false);
    const dock = document.getElementById('friend-dock');
    if (dock) dock.hidden = true;
  }

  get visible(): boolean {
    return this.open;
  }

  private onList(p: { friends: FriendView[]; incoming: string[] }): void {
    this.friends = p.friends;
    this.incoming = p.incoming;
    this.render();
  }

  private render(): void {
    const lines: string[] = [];
    lines.push(`나: ${net.name}`);
    lines.push('');
    if (this.incoming.length) {
      lines.push('[요청]');
      for (const n of this.incoming) lines.push(`  ? ${n}`);
      lines.push('');
    }
    lines.push('[친구]');
    if (!this.friends.length) lines.push('  (없음)');
    for (const f of this.friends) {
      const where = f.online ? f.mapId ?? '접속' : '오프라인';
      lines.push(`  ${f.online ? '●' : '○'} ${f.nick}  ${where}`);
    }
    this.body.setText(lines.join('\n'));
  }

  private bindHtml(scene: Phaser.Scene): void {
    const add = document.getElementById('friend-add') as HTMLInputElement | null;
    const nick = document.getElementById('friend-nick') as HTMLInputElement | null;
    const stop = (ev: Event): void => ev.stopPropagation();
    add?.addEventListener('keydown', (ev) => {
      stop(ev);
      if (ev.key === 'Enter') {
        ev.preventDefault();
        const v = add.value.trim();
        if (v) net.friendAdd(v);
        add.value = '';
      }
      if (ev.key === 'Escape') {
        ev.preventDefault();
        this.close();
        scene.game.canvas?.focus();
      }
    });
    nick?.addEventListener('keydown', (ev) => {
      stop(ev);
      if (ev.key === 'Enter') {
        ev.preventDefault();
        const v = nick.value.trim();
        if (v) net.setNick(v);
        nick.value = '';
      }
      if (ev.key === 'Escape') {
        ev.preventDefault();
        this.close();
        scene.game.canvas?.focus();
      }
    });
    document.getElementById('friend-accept')?.addEventListener('click', () => {
      const v = add?.value.trim() ?? '';
      if (v) net.friendAccept(v);
      if (add) add.value = '';
    });
    document.getElementById('friend-remove')?.addEventListener('click', () => {
      const v = add?.value.trim() ?? '';
      if (v) net.friendRemove(v);
      if (add) add.value = '';
    });
  }
}
